import React, { useState } from 'react';
import { FormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { StatusManager } from 'common/components/StatusManager';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

import { AIGeneratorError } from '../AIGeneratorError';
import { GeneratorOptions } from '../shared';

import {
  BodyText,
  LoaderContainer,
  StyledDivider,
  useDrawerStyles
} from './BaseAIGeneratorDrawer.styled';
import { isValidHTML } from './BaseAIGeneratorDrawer.utils';

/**
 * @param {Object} props
 * @param {Object} [props.autoGenerationConfig]
 * @param {Array} [props.footerExtraButtons]
 * @param {Function} props.formHook
 * @param {boolean} [props.isFormEnabled]
 * @param {undefined} [props.leftContent]
 * @param {Function} props.onClickGenerate
 * @param {Function} [props.onClickRateButton]
 * @param {Function} props.onClose
 * @param {boolean} [props.open]
 * @param {Object} props.optionsExtendedParams
 * @param {Function} [props.renderErrorState]
 * @param {Function} [props.renderInitialState]
 * @param {string} [props.submitButtonName]
 * @param {string} [props.title]
 */
export const BaseAIGeneratorDrawer = ({
  autoGenerationConfig = {},
  footerExtraButtons = [],
  formHook,
  isFormEnabled = true,
  leftContent,
  onClickGenerate,
  onClickRateButton,
  onClose,
  open = true,
  optionsExtendedParams = {},
  renderErrorState,
  renderInitialState,
  rightContentFooter = null,
  submitButtonName,
  subtitle = '',
  title
}) => {
  const classes = useDrawerStyles();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const {
    footerButtons,
    formMethods,
    getRateButtonColor,
    handleCopyToClipboard,
    handleSelectChange,
    handleSubmitRequest,
    handleThumbsDown,
    handleThumbsUp,
    uiState
  } = formHook({
    autoGenerationConfig,
    footerExtraButtons,
    onClickGenerate,
    onClickRateButton
  });

  const isResponseHtml = isValidHTML(uiState?.data?.generatedContent);
  const isGeneratingData = uiState.status === UIStatus.Loading;

  const handleConfirmDrawerClose = () => {
    setIsConfirmationDialogOpen(false);
    onClose && onClose();
  };

  const handleClose = (_event, reason) => {
    if (reason === 'backdropClick') return;
    setIsConfirmationDialogOpen(true);
  };

  const buttonDefinitions = [
    {
      props: {
        onClick: () => setIsConfirmationDialogOpen(true),
        text: strings.shared.ui.close,
        variant: 'outlined'
      },
      GridItemProps: {
        md: 'auto'
      }
    },
    ...footerButtons
  ];

  const closeDialogActions = [
    {
      variant: 'outlined',
      text: strings.ai.generators.presentationsMaker.warningCloseDialog.actions.discardAndExit,
      onClick: handleConfirmDrawerClose
    },
    {
      text: strings.ai.generators.presentationsMaker.warningCloseDialog.actions.cancel,
      onClick: () => setIsConfirmationDialogOpen(false)
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{
        title: strings.formatString(strings.ai.generators.title, { title }),
        subheader: subtitle
      }}
      FPDrawerProps={{
        open,
        classes: { paper: classes.drawerLarge },
        ModalProps: { disableEscapeKeyDown: true }
      }}
      onClose={handleClose}
      hasPaddingContent={false}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <Grid container className={classes.grid}>
        <Grid item xs={12} sm={3} className={classes.leftColumn}>
          <div className={classes.leftContainer}>{leftContent}</div>
        </Grid>

        <Grid item xs={12} sm={9} className={classes.rightColumn}>
          <div className={classes.rightContainer}>
            <Alert className={classes.alert} severity="warning">
              {strings.ai.generators.alert}
            </Alert>

            <FormContext {...formMethods}>
              <GeneratorOptions
                getRateButtonColor={getRateButtonColor}
                enabled={isFormEnabled && !isGeneratingData}
                hasGeneratedData={!!uiState.data}
                onClickGenerate={onClickGenerate}
                onClickThumbsDown={handleThumbsDown}
                onClickThumbsUp={handleThumbsUp}
                onCopyToClipboard={handleCopyToClipboard}
                onSelectChange={handleSelectChange}
                onSubmit={handleSubmitRequest}
                optionsExtendedParams={optionsExtendedParams}
                submitButtonName={submitButtonName}
              />
            </FormContext>

            <StatusManager
              status={uiState.status}
              slots={{
                Default: renderInitialState && renderInitialState(),
                Error: renderErrorState ? (
                  renderErrorState(uiState)
                ) : (
                  <AIGeneratorError status={uiState.status} error={uiState.error} />
                ),
                Loading: (
                  <LoaderContainer>
                    <LinearProgress />
                  </LoaderContainer>
                )
              }}
            >
              {uiState?.data && (
                <div className={classes.aiResponseContainer}>
                  {isResponseHtml ? (
                    <Typography
                      className={classes.assistantTypography}
                      component="div"
                      variant="body1"
                      dangerouslySetInnerHTML={{ __html: uiState.data.generatedContent }}
                    />
                  ) : (
                    <BodyText component="div" variant="body1">
                      {uiState.data.generatedContent}
                    </BodyText>
                  )}
                </div>
              )}
            </StatusManager>

            <When condition={!!rightContentFooter}>
              <StyledDivider />
              {rightContentFooter}
            </When>
          </div>
        </Grid>
      </Grid>

      <When condition={isConfirmationDialogOpen}>
        <DecisionDialog
          actions={closeDialogActions}
          isOpened
          message={strings.ai.generators.presentationsMaker.warningCloseDialog.message}
          onClose={() => setIsConfirmationDialogOpen(false)}
          showSeverityIcon
          title={strings.ai.generators.presentationsMaker.warningCloseDialog.title}
        />
      </When>
    </FPDrawer>
  );
};
